/**
 * Calculate simulated Relative Strength from an array of price values.
 *
 * Adapted from the Ruby RelativeStrength service - uses weighted strength calculation
 * with more weight on recent price points (40% most recent, 20% each older period).
 *
 * @param {number[]} prices - Array of price values (oldest to newest, left to right)
 * @param {Object} options - Optional configuration
 * @param {number} options.minStrength - Minimum weighted strength for scaling (default: 0.7)
 * @param {number} options.maxStrength - Maximum weighted strength for scaling (default: 1.6)
 * @returns {number} RS value between 0 and 100
 */
export function calculateSimulatedRS(prices, options = {}) {
  const { minStrength = 0.7, maxStrength = 1.6 } = options;

  // Need at least 5 points to calculate meaningful RS
  if (!prices || prices.length < 5) {
    return 50;
  }

  // Validate all prices are positive numbers
  if (prices.some(p => typeof p !== 'number' || p <= 0 || !isFinite(p))) {
    return 50;
  }

  const numPoints = prices.length;

  // Get price points at different "historical" offsets
  const t0 = prices[numPoints - 1];                    // Current (rightmost/newest)
  const t1 = prices[Math.floor(numPoints * 0.67)];     // ~67% back
  const t2 = prices[Math.floor(numPoints * 0.5)];      // ~50% back (middle)
  const t3 = prices[Math.floor(numPoints * 0.33)];     // ~33% back
  const t4 = prices[0];                                 // Oldest (leftmost)

  // Calculate ratios (current price / historical price)
  // Ratio > 1 means price went up, < 1 means price went down
  const ratios = {
    p0: t0 / t4,  // Longest timeframe (vs oldest)
    p1: t0 / t3,  // Second longest
    p2: t0 / t2,  // Middle timeframe
    p3: t0 / t1   // Most recent timeframe (highest weight)
  };

  // Weights: most recent period gets 40%, others get 20%
  const weights = { p0: 0.2, p1: 0.2, p2: 0.2, p3: 0.4 };

  // Calculate weighted strength
  const weightedStrength = Object.keys(weights).reduce((sum, period) => {
    return sum + weights[period] * ratios[period];
  }, 0);

  // Map weighted strength to 0-100 scale
  const normalized = (weightedStrength - minStrength) / (maxStrength - minStrength);
  const rs = normalized * 100;

  // Clamp to 0-100 range
  return Math.max(0, Math.min(100, rs));
}
